import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spin } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql } from '@apollo/client';

import DataGrid from '../../components/DataGrid';
import Error from '../../components/Error';
import EmployeeExport from './employeeExport';

import {
  updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
  addOrGotoEmployeeTab,
  updateVisibleColumns,
} from '../../store/reducers/employeeSlice';
import { EMPLOYEE_LIST_FIELDS } from '../../apollo/fragments';

const EMPLOYEES_QUERY = gql`
  ${EMPLOYEE_LIST_FIELDS}
  query Employees(
    $gridId: String
    $first: Int
    $page: Int
    $filters: [QueryGetEmployeesFilter]
    $sort: QueryGetEmployeesSorter
  ) {
    getEmployees(
      gridId: $gridId
      first: $first
      page: $page
      filters: $filters
      sort: $sort
    ) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...EmployeeListFields
      }
    }
  }
`;

const EmployeeList = ({ gridId }) => {
  const {
    metaData,
    grids,
    lastCreatedEmployeeId,
    employeeDidRelieved,
    employeeDidRejoined,
  } = useSelector((state) => state.employee);
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);

  const queryVariables = useMemo(() => {
    return {
      gridId,
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    gridId,
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);
  // const queryVariables = {
  //   gridId,
  //   first: grid.metaData.pagination.pageSize,
  //   page: grid.metaData.pagination.page,
  //   filters: grid.metaData.filters,
  //   sort: grid.metaData.sorting,
  // };

  const { loading, data, error, refetch } = useQuery(EMPLOYEES_QUERY, {
    variables: queryVariables,
  });

  console.log('ERROR', error);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const onEmployeeClick = (employee) => {
    console.log('onEmployeeClick///', employee);
    const tab = {
      title: employee.emp_id,
      key: `empTab-${employee.id}`,
      data: employee,
      closable: true,
    };
    dispatch(addOrGotoEmployeeTab({ tab }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'emp_id',
      key: 'emp_id',
      serverKey: 'EMP_ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      render: (value, record) => {
        return (
          <Button
            type="link"
            onClick={() => onEmployeeClick(record)}
            style={{ padding: '0' }}
          >
            {value}
          </Button>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'first_name',
      key: 'first_name',
      serverKey: 'NAME',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      render: (value, record) => {
        return record && `${record.first_name} ${record.last_name}`;
      },
    },
    {
      title: 'Designation',
      dataIndex: ['designation', 'name'],
      key: 'designation',
      serverKey: 'DESIGNATION',
      sorter: true,
      canExclude: true,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Portfolio',
      dataIndex: ['portfolio', 'code'],
      key: 'portfolio',
      serverKey: 'PORTFOLIO',
      sorter: true,
      canExclude: true,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Product Type',
      dataIndex: ['productType', 'code'],
      key: 'productType',
      serverKey: 'PRODUCT',
      sorter: true,
      canExclude: true,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Stage',
      dataIndex: ['stage', 'name'],
      key: 'stage',
      serverKey: 'STAGE',
      sorter: true,
      canExclude: true,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Phone 1',
      dataIndex: ['contact', 'phone_1'],
      key: 'phone_1',
      serverKey: 'PHONE_1',
      sorter: true,
      canExclude: true,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'D.O.J',
      dataIndex: 'date_of_joining',
      key: 'date_of_joining',
      serverKey: 'DOJ',
      sorter: true,
      canExclude: true,
      visible: true,
      format: 'date',
      dataType: 'date',
    },
    {
      title: 'Location',
      dataIndex: ['location', 'name'],
      key: 'location',
      serverKey: 'LOCATION',
      sorter: true,
      canExclude: true,
      visible: true,
      dataType: 'string',
    },
    {
      title: 'ID Card',
      dataIndex: ['identityCard', 'card_number'],
      key: 'card_number',
      serverKey: 'IDCARD',
      sorter: true,
      canExclude: true,
      visible: true,
      dataType: 'string',
    },
    {
      title: 'Exp. Date',
      dataIndex: ['identityCard', 'card_validity_end'],
      key: 'card_validity_end',
      serverKey: 'EXPDATE',
      sorter: true,
      canExclude: true,
      visible: true,
      format: 'date',
      dataType: 'date',
    },
    {
      title: 'Region',
      dataIndex: ['location', 'region', 'name'],
      key: 'region',
      serverKey: 'REGION',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'Phone 2',
      dataIndex: ['contact', 'phone_2'],
      key: 'phone_2',
      serverKey: 'PHONE_2',
      sorter: true,
      canExclude: true,
      visible: false,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Email',
      dataIndex: ['contact', 'email'],
      key: 'email',
      serverKey: 'EMAIL',
      sorter: true,
      canExclude: true,
      visible: false,
      ellipsis: true,
      dataType: 'string',
    },

    {
      title: 'DRA Number',
      dataIndex: ['identityCard', 'dra_number'],
      key: 'dra_number',
      serverKey: 'DRA_NUMBER',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'DRA Training start',
      dataIndex: ['identityCard', 'dra_training_start'],
      key: 'dra_training_start',
      serverKey: 'DRA_TRAINING_START',
      sorter: true,
      canExclude: true,
      visible: false,
      format: 'date',
      dataType: 'date',
    },
    {
      title: 'DRA Training end',
      dataIndex: ['identityCard', 'dra_training_end'],
      key: 'dra_training_end',
      serverKey: 'DRA_TRAINING_END',
      sorter: true,
      canExclude: true,
      visible: false,
      format: 'date',
      dataType: 'date',
    },
    {
      title: 'DRA Status',
      dataIndex: ['identityCard', 'dra_status'],
      key: 'dra_status',
      serverKey: 'DRA_STATUS',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'PVC Card',
      dataIndex: ['identityCard', 'pvc_number'],
      key: 'pvc_number',
      serverKey: 'PVC_CARD',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'PVC Exp. Date',
      dataIndex: ['identityCard', 'pvc_validity_end'],
      key: 'pvc_validity_end',
      serverKey: 'PVC_EXPDATE',
      sorter: true,
      canExclude: true,
      visible: false,
      format: 'date',
      dataType: 'date',
    },
    {
      title: 'RAC Mobility Id',
      dataIndex: ['identityCard', 'rac_mobility_id'],
      key: 'rac_mobility_id',
      serverKey: 'RAC_MOBILITY_ID',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'CC Mobility Id',
      dataIndex: ['identityCard', 'cc_mobility_id'],
      key: 'cc_mobility_id',
      serverKey: 'CC_MOBILITY_ID',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'DID Number',
      dataIndex: ['identityCard', 'did_number'],
      key: 'did_number',
      serverKey: 'DID_NUMBER',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'Ins. Provider',
      dataIndex: ['healthInsurance', 'provider'],
      key: 'provider',
      serverKey: 'PROVIDER',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'Policy Start',
      dataIndex: ['healthInsurance', 'policy_start_date'],
      key: 'policy_start_date',
      serverKey: 'POLICY_START',
      sorter: true,
      canExclude: true,
      visible: false,
      format: 'date',
      dataType: 'date',
    },
    {
      title: 'Policy End',
      dataIndex: ['healthInsurance', 'policy_end_date'],
      key: 'policy_end_date',
      serverKey: 'POLICY_END',
      sorter: true,
      canExclude: true,
      visible: false,
      format: 'date',
      dataType: 'date',
    },
    {
      title: 'Ins. Type',
      dataIndex: ['healthInsurance', 'type'],
      key: 'type',
      serverKey: 'INSURANCE_TYPE',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
    },
    {
      title: 'Sum Insured',
      dataIndex: ['healthInsurance', 'sum_insured'],
      key: 'sum_insured',
      serverKey: 'SUM_INSURED',
      sorter: true,
      canExclude: true,
      visible: false,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
  ];

  const columnsForExport = [
    ...columns,
    {
      title: 'PAN',
      key: 'pan',
      serverKey: 'PAN',
      dataType: 'string',
    },
    {
      title: 'Bank Name',
      key: 'bank_name',
      serverKey: 'BANK_NAME',
      dataType: 'string',
    },
    {
      title: 'Bank Account No',
      key: 'bank_account_number',
      serverKey: 'ACCOUNT_NUMBER',
      dataType: 'string',
    },
    {
      title: 'Bank Branch',
      key: 'bank_branch',
      serverKey: 'BANK_BRANCH',
      dataType: 'string',
    },
    {
      title: 'Bank IFSC Code',
      key: 'ifsc',
      serverKey: 'BANK_IFSC',
      dataType: 'string',
    },
    {
      title: 'Date of Relieve',
      key: 'date_of_relieve',
      serverKey: 'DATE_OF_RELIEVE',
      dataType: 'date',
    },
    {
      title: 'Address 1',
      key: 'address_1',
      serverKey: 'ADDRESS_1',
      dataType: 'string',
    },
    {
      title: 'Address 2',
      key: 'address_2',
      serverKey: 'ADDRESS_2',
      dataType: 'string',
    },
    {
      title: 'City',
      key: 'city',
      serverKey: 'CITY',
      dataType: 'string',
    },
    {
      title: 'State',
      key: 'state',
      serverKey: 'STATE',
      dataType: 'string',
    },
    {
      title: 'Pincode',
      key: 'pincode',
      serverKey: 'PINCODE',
      dataType: 'string',
    },
    {
      title: 'Ref. Name',
      key: 'reference_name',
      serverKey: 'REFERENCE_NAME',
      dataType: 'string',
    },
    {
      title: 'Ref. Contact',
      key: 'reference_contact',
      serverKey: 'REFERENCE_CONTACT',
      dataType: 'string',
    },
  ];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  // useEffect(() => {
  //   if (metaData) {
  //     console.log('refetch for metaData with///', queryVariables);
  //     refetch(queryVariables);
  //   }
  // }, [dispatch, metaData]);

  useEffect(() => {
    if (lastCreatedEmployeeId || employeeDidRelieved || employeeDidRejoined) {
      console.log('refetch for newEMployee with///', queryVariables);
      refetch(queryVariables);
    }
  }, [
    dispatch,
    refetch,
    queryVariables,
    lastCreatedEmployeeId,
    employeeDidRelieved,
    employeeDidRejoined,
  ]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateTableMetaData({
        metaData: {
          ...metaData,
          ...updatedMetaData,
        },
      })
    );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  return (
    <Spin spinning={loading}>
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {error && <Error message={error.message} />}

        {data && data.getEmployees && (
          <DataGrid
            dataSource={data.getEmployees.data}
            columns={columns}
            rowSelection={rowSelection}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.getEmployees.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onExport={(onFinish) => {
              setShowExportModal(true);
            }}
            onColumnVisibilityUpdate={(columns) => {
              console.log('UPDTED columns //////', columns);
              const visibleColumns = columns.map((column) => {
                return {
                  column: column.serverKey,
                  value: column.visible,
                };
              });
              dispatch(updateVisibleColumns({ gridId, visibleColumns }));
            }}
          />
        )}
      </Card>
      <EmployeeExport
        grid={grid}
        columns={columnsForExport}
        showModal={showExportModal}
        setShowModal={setShowExportModal}
      />
    </Spin>
  );
};

export default EmployeeList;
